/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMemo } from 'react'

import Feature from '@modules/utils/feature'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useConfig } from '@modules/cui/ConfigContext'

import { isFeatureActivated } from '@/store' // eslint-disable-line import/no-restricted-paths

/**
 * Hook to check if the Cross-Project Search (CPS) feature is active.
 *
 * This checks both:
 * 1. The config-based feature flag (Feature.cps)
 * 2. The LaunchDarkly flag ('cross-project-search') - only in environments where LaunchDarkly is enabled
 *
 * For environments without LaunchDarkly (e.g., adminconsole), only the config flag is checked.
 *
 * @returns {boolean} true if the config feature flag is enabled and (if LaunchDarkly is available) the LD flag is also enabled
 */
export function useIsCpsFeatureActive(): boolean {
  const isLaunchDarklyEnabled = Boolean(useConfig(Feature.launchdarklyId))
  const [isFlagsUsable, flags] = useFlagsWhenLoaded()
  const isConfigEnabled = isFeatureActivated(Feature.cps)

  return useMemo(() => {
    if (!isConfigEnabled) {
      return false
    }

    // If LaunchDarkly is not enabled (e.g., adminconsole), just return the config flag status
    if (!isLaunchDarklyEnabled) {
      return true
    }

    // If LaunchDarkly flags aren't loaded yet, return false
    if (!isFlagsUsable) {
      return false
    }

    // Check the LaunchDarkly flag - default to false if not present
    return Boolean(flags.crossProjectSearch)
  }, [isConfigEnabled, isLaunchDarklyEnabled, isFlagsUsable, flags.crossProjectSearch])
}
